import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, List, Row, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { toUploadStatusColor } from "../../constants";
import { File as ApiFile, SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useEffect, useRef, useState } from "react";
import Dragger from "antd/es/upload/Dragger";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { AssetUploadStatuses, WorkOrder } from "../../api/models";

type WorkOrderUploadAssetsProps = {
  workOrder?: WorkOrder,
  onClose: () => void,
  onSave: (workOrder: WorkOrder) => void
};

type WorkOrderAsset = {
  path: string,
  uploaded: boolean
};

type WorkOrderAssets = {
  [path: string]: WorkOrderAsset
};

function createWorkOrderAssets(assetUploadStatuses?: AssetUploadStatuses): WorkOrderAssets {
  const assets: WorkOrderAssets = {};
  for (const file of assetUploadStatuses?.files ?? []) {
    assets[file.path] = {
      path: file.path,
      uploaded: file.ready
    };
  }

  return assets;
}

export default function (p: WorkOrderUploadAssetsProps) {
  const [assets, setAssets] = useState<WorkOrderAssets>({});
  const [assetInitializationStatus, setAssetInitializationStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [assetSubmitStatus, setAssetSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [confirmSubmitStatus, setConfirmSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [error, setError] = useState<string>();
  const [activeAsset, setActiveAsset] = useState<WorkOrderAsset>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { api } = useContext(ApiContext);
  const isInitializing = assetInitializationStatus === SubmitStatus.Submitting;
  const isAssetSubmitting = assetSubmitStatus === SubmitStatus.Submitting;
  const isConfirmSubmitting = confirmSubmitStatus === SubmitStatus.Submitting;
  const canConfirm = !isInitializing &&
    !isAssetSubmitting &&
    !isConfirmSubmitting &&
    Object.values(assets).every(a => a.uploaded);
  const canUploadAsset = !isInitializing && !isAssetSubmitting && !isConfirmSubmitting;

  const uploadFiles = (filesToUpload: ApiFile[]) => {
    if (filesToUpload.length === 0) {
      return;
    }

    setAssetSubmitStatus(SubmitStatus.Submitting);
    api?.postWorkOrderDynamicAssets(p.workOrder?.workOrderID!, filesToUpload).then(response => {
      if (!response.good() || !response.body) {
        setError("Failed to upload the asset(s).");
        return;
      }

      setAssets(createWorkOrderAssets(response.body));
      setError(undefined);
    }).catch(_ => {
      setError("Failed to upload the asset(s).");
    }).finally(() => {
      setAssetSubmitStatus(SubmitStatus.Idle);
    });
  };

  const isZipFile = (fileName: string) => fileName.toLowerCase().endsWith(".zip");

  const onAssetTagClicked = (asset: WorkOrderAsset) => {
    if (!canUploadAsset) {
      return;
    }

    setActiveAsset(asset);
    fileInputRef.current!.value = "";
    fileInputRef.current!.click();
  };

  // Load the initial status of the work order's dynamic assets.
  useEffect(() => {
    api?.getWorkOrderDynamicAssets(p.workOrder?.workOrderID!).then(response => {
      if (!response.good()) {
        setError("Failed to get the work order assets.");
        return;
      }

      setAssets(createWorkOrderAssets(response.body!));
    }).catch(() => {
      setError("Failed to get the work order assets.");
    }).finally(() => {
      setAssetInitializationStatus(SubmitStatus.Idle);
    });
  }, []);

  return (
    <>
      <Title style={titleStyle} level={4}>Upload Assets</Title>
      <Form
        layout="vertical"
        colon={false}>
        <Form.Item label="Name">
          <Paragraph strong>{p.workOrder?.name}</Paragraph>
        </Form.Item>
        <Form.Item label="Render Count">
          <Paragraph strong>{p.workOrder?.renderCount}</Paragraph>
        </Form.Item>
        <Form.Item label="Upload Assets">
          <Dragger
            name="file"
            listType={undefined}
            multiple={true}
            action={undefined}
            showUploadList={false}
            fileList={[]}
            beforeUpload={() => false}
            disabled={false}
            onChange={e => {
              for (const file of e.fileList) {
                if (!file.originFileObj) {
                  setError(`File '${file.name}' could not be read.`);
                  return;
                }
              }

              const filesToUpload: ApiFile[] = [];
              for (const file of e.fileList) {
                const isZip = isZipFile(file.name);
                const isKnownAsset = assets[file.name] !== undefined;
                if (!isZip && !isKnownAsset) {
                  continue;
                }

                filesToUpload.push({
                  fileName: file.name,
                  fileData: file.originFileObj!
                });
              }

              uploadFiles(filesToUpload);
            }}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag files to this area to upload.</p>
          </Dragger>
        </Form.Item>
        <Form.Item label="Assets">
          <List
            bordered={false}
            footer={false}
            loading={isInitializing}
            pagination={{ pageSize: 5, hideOnSinglePage: Object.keys(assets).length < 6 }}
            size="small"
            dataSource={Object.values(assets)}
            renderItem={(item: WorkOrderAsset) =>
              <List.Item key={item.path}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{item.path}</span>
                  <Tag
                    color={toUploadStatusColor(item.uploaded)}
                    style={{ cursor: canUploadAsset ? "pointer" : "default" }}
                    onClick={() => onAssetTagClicked(item)}>
                    {item.uploaded ? "Uploaded" : "Not Uploaded"}
                  </Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (!file || !activeAsset || isZipFile(file.name)) {
                setActiveAsset(undefined);
                return;
              }

              uploadFiles([{ fileName: activeAsset.path, fileData: file }]);
              setActiveAsset(undefined);
            }}
          />
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            disabled={isAssetSubmitting || isConfirmSubmitting}
            onClick={p.onClose}>
            Close
          </Button>
          <Button
            type="primary"
            disabled={!canConfirm}
            loading={isConfirmSubmitting}
            style={buttonStyle}
            onClick={() => {
              setConfirmSubmitStatus(SubmitStatus.Submitting);
              api?.postWorkOrderDynamicAssetConfirm(p.workOrder?.workOrderID!).then(response => {
                if (!response.good()) {
                  setError("Failed to confirm the assets.");
                  return;
                }

                return api?.getWorkOrder(p.workOrder?.workOrderID!).then(workOrderResponse => {
                  if (!workOrderResponse.good() || !workOrderResponse.body) {
                    setError("Failed to get the work order.");
                    return;
                  }

                  p.onSave(workOrderResponse.body);
                });
              }).catch(() => {
                setError("Failed to confirm the assets.");
              }).finally(() => {
                setConfirmSubmitStatus(SubmitStatus.Idle);
              });
            }}>
            Confirm
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}
